import type { TGender } from '~constants/gender';
import { RegisterInbound, RegisterOutbound } from '~features/annals/names';
import type {
	TShopItem,
	TShopNeuronsPack,
	TShopSubscription,
} from '~services/api/credits/types';
import { getRegistrationMarkersAsync } from '~shared/api/origin/analytics/registration';
import { LoggerMessages } from '~shared/services/logger';
import { sendMetricToAPI } from '~shared/services/metrics/sendMetricToAPI';
import { transformShopItemToGTMData } from '~utils/analytics';
import { calculateAge } from '~utils/dates';

import * as AUAnalytics from './AUAnalytics';
import {
	REGISTRATION_FORM_MAIN_ID,
	REGISTRATION_FORM_MAIN_NAME,
} from './constants';
import { type GtmPayload, isHasOffers, maskEmailInPayload } from './utils';

type Markers = Record<string, unknown>;

class GTM {
	private user_id: string | null = null;
	private email: string | null = null;
	annalsRegistrationPromise: Promise<Markers> | null = null;

	setUserId = (id: string) => {
		if (this.user_id !== id) {
			this.user_id = id;
			AUAnalytics.updateUserId(id);
			this.annalsRegistrationPromise = this.getAnnalsRegistrationPromise();
		}
	};
	setEmail = (email: string) => {
		this.email = email;
	};

	private getAnnalsRegistrationPromise = () => {
		if (!this.user_id) {
			return Promise.reject('No user ID available');
		}

		return getRegistrationMarkersAsync(this.user_id);
	};

	pushToDataLayer = (
		payload: GtmPayload,
		metrics: {
			marketing?: boolean;
			tags?: string[];
		} = {}
	) => {
		if (!window.dataLayer) {
			return;
		}

		window.dataLayer.push(payload);

		const maskedPayload = maskEmailInPayload(payload);

		const { tags = [], marketing = false } = metrics;

		const metricPayload = {
			name: `gtm.dataLayer.push`,
			tags: [payload.event, ...tags],
			raw: maskedPayload,
		};

		if (!marketing) {
			void sendMetricToAPI(metricPayload, LoggerMessages.MetricGtmFailed);
		} else {
			this.annalsRegistrationPromise
				?.then(
					(data) =>
						isHasOffers(data) && metricPayload.tags.push('marketing:has_offers')
				)
				.catch(() => metricPayload.tags.push('marketing:not_ready'))
				.finally(
					() =>
						void sendMetricToAPI(metricPayload, LoggerMessages.MetricGtmFailed)
				);
		}
	};

	userAuthorized = () => {
		this.pushToDataLayer({
			event: 'event_authorized_changeDL',
			user_id: this.user_id,
			data: {
				email: this.email,
			},
		});
	};
	login() {
		AUAnalytics.callAction('login');
	}
	userSignedUp = (
		method: string,
		afid?: string,
		birthday?: string,
		name?: string,
		registerOutbound = RegisterOutbound.NoneIdentified,
		formName = REGISTRATION_FORM_MAIN_NAME,
		formId = REGISTRATION_FORM_MAIN_ID
	) => {
		const age = birthday ? calculateAge(birthday) : undefined;
		this.pushToDataLayer(
			{
				event: 'event_form_action_changeDL',
				user_id: this.user_id,
				afid,
				form: {
					name: formName,
					id: formId,
				},
				data: {
					name,
					age,
				},
				method,
				registration_out: registerOutbound,
			},
			{
				marketing: true,
				tags: ['signup'],
			}
		);

		AUAnalytics.callAction('regnotfin');
	};

	registerFormShown = (
		registerInbound = RegisterInbound.NoneIdentified,
		botId: string | undefined
	) => {
		this.pushToDataLayer({
			event: 'registration_shown',
			user_id: this.user_id,
			registration_in: registerInbound,
			character: botId,
		});
	};

	generateLead = (
		afid?: string,
		registerOutbound = RegisterOutbound.NoneIdentified,
		formName = REGISTRATION_FORM_MAIN_NAME,
		formId = REGISTRATION_FORM_MAIN_ID
	) => {
		this.pushToDataLayer({
			event: 'event_registered_changeDL',
			user_id: this.user_id,
			afid,
			form: {
				name: formName,
				id: formId,
			},
			data: {
				email: this.email,
			},
			registration_out: registerOutbound,
		});
	};

	userConfirmedEmail = (
		afid: string,
		email: string,
		birthday?: string,
		gender?: TGender,
		country?: string
	) => {
		const age = birthday ? calculateAge(birthday) : undefined;
		const data = {
			email,
			gender: gender?.toString(),
			age,
		};

		this.pushToDataLayer(
			{
				event: 'confirm_email',
				user_id: this.user_id,
				country,
				afid,
				data,
			},
			{
				marketing: true,
			}
		);

		AUAnalytics.callAction('regfin');
	};

	userRedirectedFromNotification = (
		afid: string,
		email: string,
		birthday?: string,
		gender?: TGender,
		country?: string
	) => {
		const age = birthday ? calculateAge(birthday) : undefined;
		const data = {
			email,
			gender: gender?.toString(),
			age,
		};

		this.pushToDataLayer({
			event: 'notification',
			user_id: this.user_id,
			country: country ?? 'N/A',
			afid,
			data,
		});
	};

	ageConfirmed = () => {
		this.pushToDataLayer({
			event: 'content_agreed',
			user_id: this.user_id,
		});
	};

	registrationFormFill = (
		field: string,
		formName = REGISTRATION_FORM_MAIN_NAME,
		formId = REGISTRATION_FORM_MAIN_ID
	) => {
		this.pushToDataLayer({
			event: 'event_form_action_changeDL',
			user_id: this.user_id,
			registration_forms: {
				name: 'start-fill',
				form: {
					name: formName,
					id: formId,
					field,
				},
			},
		});
	};

	characterChosen = (botId: string) => {
		this.pushToDataLayer({
			event: 'character_chosen',
			user_id: this.user_id,
			character: botId,
		});
	};

	viewSubscription = (items: TShopSubscription[]) => {
		this.pushToDataLayer({
			event: 'view_item_list',
			user_id: this.user_id,
			ecommerce: {
				items: items.map((item, index) => ({
					...transformShopItemToGTMData(item),
					index: index + 1,
				})),
			},
		});
	};

	userDidReceiveSextingMessage = (botId: string) => {
		this.pushToDataLayer({
			event: 'sexting_message',
			user_id: this.user_id,
			value: botId,
		});
	};

	viewNeurons = (items: TShopNeuronsPack[]) => {
		this.pushToDataLayer({
			event: 'view_item_list',
			user_id: this.user_id,
			ecommerce: {
				items: items.map((item, index) => ({
					...transformShopItemToGTMData(item),
					index: index + 1,
				})),
			},
		});
	};

	selectItem = (item: TShopItem, index: number) => {
		try {
			this.pushToDataLayer({
				event: 'select_item',
				user_id: this.user_id,
				ecommerce: {
					value: item.price,
					items: [
						{
							...transformShopItemToGTMData(item),
							index: index + 1,
							quantity: '1',
						},
					],
				},
			});
		} catch {}
	};

	beginCheckout = (item: TShopItem) => {
		try {
			this.pushToDataLayer({
				event: 'begin_checkout',
				user_id: this.user_id,
				ecommerce: {
					items: [
						{
							...transformShopItemToGTMData(item, true),
							quantity: '1',
						},
					],
				},
			});
		} catch {}
	};

	startFillingPaymentForm() {
		this.pushToDataLayer({
			event: 'event_form_action_changeDL',
			registration_forms: {
				name: 'start-fill',
				form: { name: 'purchase-form' },
			},
		});
	}

	purchase = ({
		item,
		transactionId,
		isFirst = false,
		coupon,
	}: {
		item: TShopItem;
		transactionId: string;
		isFirst: boolean;
		coupon?: string;
	}) => {
		const tags = [
			item.meta.Subscription ? 'variant:auto' : 'variant:non-recurring',
		];
		if (isFirst) {
			tags.push('first_purchase');
		}
		void this.pushToDataLayer(
			{
				event: 'purchase',
				user_id: this.user_id,
				ecommerce: {
					value: item.discountedPrice ?? item.price,
					coupon: coupon ?? '',
					currency: item.currency.toUpperCase(),
					transaction_id: transactionId,
					items: [
						{
							...transformShopItemToGTMData(item, true),
							coupon: coupon ?? '',
							item_variant: item.meta.Subscription ? 'auto' : 'non-recurring',
							...(isFirst ? { item_category2: 'payment-1st' } : {}),
							quantity: '1',
						},
					],
				},
			},
			{
				marketing: true,
				tags,
			}
		);

		AUAnalytics.callAction('purch1');
	};

	createFreeImage = (generated: boolean) => {
		this.pushToDataLayer({
			event: 'create_free_image_in_na',
			generated,
			user_id: this.user_id,
		});
	};
}

export const GTMAnalytics = new GTM();
